import React, { useCallback, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spin } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import { Routes } from '@share/constants';
import { BlueButton } from '@share/components';
import { FunctionalRootState } from '@share/utils';
import { fetchCertificateById } from '@share/store/slices';
import { useCertificateClubSiteId } from '@share/components';

import { CertificateTemplateDetailView } from '../certificate-template-detail-view';

import { mapCertificateDetailsToDetailViewData } from './utils';
import styles from './styles.module.scss';

export function CertificateDetailWrapper(): React.ReactElement {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id, accountUsername } = useParams<{ id: string; accountUsername?: string }>();
  const { certificate, loading, error } = useSelector(
    (state: FunctionalRootState) => state.CertificateByIdStore,
  );
  const organizationId = useCertificateClubSiteId();

  const certificatesPath = accountUsername
    ? `/${accountUsername}${Routes.Certificates}`
    : Routes.Certificates;

  const fetchCertificate = useCallback(() => {
    if (id) {
      dispatch(fetchCertificateById({ id, clubSiteId: organizationId }));
    }
  }, [dispatch, id, organizationId]);

  useEffect(() => {
    fetchCertificate();
  }, [fetchCertificate]);

  const handleBackClick = useCallback(() => {
    history.push(certificatesPath);
  }, [history, certificatesPath]);

  const handleReloadClick = useCallback(() => {
    fetchCertificate();
  }, [fetchCertificate]);

  const isNotAvailable = error?.code === 'certificate_unavailable';
  const backLabel = intl.formatMessage({ id: 'certificates.backTo' });

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.stateBlock} role="status" aria-busy="true">
          <Spin size="large" />
          <span className={styles.loadingText}>
            <FormattedMessage id="certificates.loading" />
          </span>
        </div>
      </div>
    );
  }
  if (error || !certificate) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <a
            href={certificatesPath}
            className={styles.backLink}
            onClick={(e) => {
              e.preventDefault();
              handleBackClick();
            }}
            aria-label={backLabel}
          >
            {backLabel}
          </a>
          <div className={styles.stateBlock}>
            <p className={styles.errorText}>
              {isNotAvailable
                ? intl.formatMessage({ id: 'certificates.noLongerAvailable' })
                : error?.title ?? intl.formatMessage({ id: 'certificates.notFound' })}
            </p>
            {!isNotAvailable && (
              <BlueButton onClick={handleReloadClick}>
                <FormattedMessage id="certificates.reloadCertificate" />
              </BlueButton>
            )}
          </div>
        </div>
      </div>
    );
  }

  const detailViewData = mapCertificateDetailsToDetailViewData(certificate);

  return (
    <CertificateTemplateDetailView
      {...detailViewData}
      accountUsername={accountUsername ?? ''}
      onBackClick={handleBackClick}
    />
  );
}
